import Form from '@form/Form';
import RecordCreateSubformVehicle from '@csp-app/lib/damage/record/create/RecordCreateSubformVehicle';
import ClaimTypeEnum from '@js/enums/damage/ClaimTypeEnum';
import GenderEnum from '@js/enums/GenderEnum';

interface FirmPermissionsInterface {
  createCasualtyClaims: boolean,
  createComprehensiveClaims: boolean,
  createComprehensiveClaimsWithMinimalData: boolean,
  createDeliveryClaims: boolean,
}

export default class RecordCreateForm extends Form {
  private firmPermissions: FirmPermissionsInterface = {
    createCasualtyClaims: true,
    createComprehensiveClaims: false,
    createComprehensiveClaimsWithMinimalData: false,
    createDeliveryClaims: false,
  };

  private ignoreMinimalRecordPermissions = false;

  private subformVehicle: RecordCreateSubformVehicle | null = null;

  public forceMinimalRecordCreation(): void {
    this.ignoreMinimalRecordPermissions = true;

    this.setProperty('minimalRecordCreation', true);
  }

  public getSubform(identifier: string): Form {
    if (identifier === 'vehicle') {
      throw 'The vehicle subform has a dedicated form class. Call getSubformVehicle() instead!';
    }
    return super.getSubform(identifier);
  }

  public getSubformVehicle(): RecordCreateSubformVehicle {
    if (this.subformVehicle) {
      return this.subformVehicle;
    }
    const identifier = 'vehicle';
    const subformVehicle = new RecordCreateSubformVehicle(this.getDataOfSubform(identifier));
    subformVehicle.toRecordCreateSubform(identifier, this);
    this.subformVehicle = subformVehicle;
    return this.subformVehicle;
  }

  /**
   * @return true if customer data have been pre-filled, false if not
   */
  public handleClaimTypeChange(currentUser: Record<string, unknown>, selectedFirm: Record<string, unknown>): boolean {
    this.resetMinimalRecordCreationIfNotAllowed();
    return this.setFirstPartyDamageIfDeliveryClaim(currentUser, selectedFirm);
  }

  public isCasualtyClaimTypeAllowed(): boolean {
    return this.firmPermissions.createCasualtyClaims;
  }

  public isCasualtyClaimTypeSelected(): boolean {
    return this.getSubform('claimType').getDataOfProperty('insuranceType') === ClaimTypeEnum.CASUALTY;
  }

  public isComprehensiveClaimTypeAllowed(): boolean {
    return this.firmPermissions.createComprehensiveClaims;
  }

  public isComprehensiveClaimTypeSelected(): boolean {
    return this.getSubform('claimType').getDataOfProperty('insuranceType') === ClaimTypeEnum.COMPREHENSIVE;
  }

  public isDeliveryClaimTypeAllowed(): boolean {
    return this.firmPermissions.createDeliveryClaims;
  }

  public isDeliveryClaimTypeSelected(): boolean {
    return this.getSubform('claimType').getDataOfProperty('insuranceType') === ClaimTypeEnum.DELIVERY;
  }

  public isMinimalRecordCreationAllowed(): boolean {
    if (this.ignoreMinimalRecordPermissions) {
      return true;
    }
    if (this.firmPermissions.createComprehensiveClaimsWithMinimalData) {
      return true;
    }
    return !this.isComprehensiveClaimTypeSelected();
  }

  public minimalRecordCreationDisable(): void {
    this.getSubform('damage').setAllowedEmptyProperties([]);
    this.getSubform('comprehensiveInsurancePolicy').setAllowedEmptyProperties([]);
    this.getSubform('adversary').setAllowedEmptyProperties([]);
    this.getSubform('adversary').getSubform('insurancePolicy').setAllowedEmptyProperties([]);
    this.getSubform('customer').setAllowedEmptyProperties([]);
    this.getSubformVehicle().setAllowedEmptyProperties([]);
    this.getSubform('differingPolicyholder').setAllowedEmptyProperties([]);
  }

  public minimalRecordCreationEnable(): void {
    this.getSubform('damage').setAllowedEmptyProperties(['accidentDateTime', 'deliveryFirmId']);
    this.getSubform('comprehensiveInsurancePolicy').setAllowedEmptyProperties(['parentInsuranceId']);

    this.getSubform('adversary').setAllowedEmptyProperties([
      'gender',
      'companyName',
      'firstName',
      'lastName',
      'cargo',
      'registrationDate',
      'mileage',
    ]);
    this.getSubform('adversary').getSubform('insurancePolicy').setAllowedEmptyProperties(['parentInsuranceId']);

    this.getSubform('customer').setAllowedEmptyProperties([
      'gender',
      'companyName',
      'firstName',
      'lastName',
      'street',
      'postalCode',
      'place',
      'phoneGeneric',
    ]);

    this.getSubformVehicle().setAllowedEmptyProperties([
      'licensePlate',
      'model',
      'registrationDate',
      'mileage',
    ]);

    this.getSubform('differingPolicyholder').setAllowedEmptyProperties([
      'gender',
      'companyName',
      'firstName',
      'lastName',
      'street',
      'postalCode',
      'place',
      'mobile',
    ]);

    if (this.getProperty('recordCreationByCustomer')) {
      this.getSubform('customer').setAllowedEmptyProperties([
        'gender',
        'street',
        'postalCode',
        'place',
      ]);

      this.getSubformVehicle().setAllowedEmptyProperties([
        'model',
        'registrationDate',
        'mileage',
      ]);
    }
  }

  public resetFirstPartyDamageCustomerFields(): void {
    // reset customer data input fields that would be auto-filled when selecting first party damage
    const customerForm = this.getSubform('customer');
    customerForm.setProperty('firstName', '')
    customerForm.setProperty('lastName', '')
    customerForm.setProperty('companyName', '')
    customerForm.setProperty('place', '')
    customerForm.setProperty('phoneGeneric', '')
    customerForm.setProperty('postalCode', '')
    customerForm.setProperty('street', '')
    customerForm.setProperty('email', '')
  }

  public setFirmPermissions(firmPermissions: FirmPermissionsInterface): void {
    this.firmPermissions = firmPermissions;

    if (!this.isComprehensiveClaimTypeAllowed() && this.isCasualtyClaimTypeAllowed()) {
      this.getSubform('claimType').setProperty('insuranceType', ClaimTypeEnum.CASUALTY);
    }

    if (!this.isCasualtyClaimTypeAllowed() && this.isComprehensiveClaimTypeAllowed()) {
      this.getSubform('claimType').setProperty('insuranceType', ClaimTypeEnum.COMPREHENSIVE);
    }

    this.resetMinimalRecordCreationIfNotAllowed();
  }

  public setIsFleetOrMajorCustomer(isFleetOrMajorCustomer: boolean): void {
    this.setProperty('isFleetOrMajorCustomer', isFleetOrMajorCustomer);
  }

  public setRecordCreationByCustomer(isRecordCreationByCustomer: boolean): void {
    this.setProperty('recordCreationByCustomer', isRecordCreationByCustomer);
  }

  public updateAllowedEmptyPropertiesForFleetOrMajorCustomer(): void {
    this.getSubform('customer').setAllowedEmptyProperties([
      'gender',
      'companyName',
      'street',
      'postalCode',
      'place',
    ]);
  }

  public updateFirstPartyDamage(currentUser: Record<string, unknown>, selectedFirm: Record<string, unknown>): void {
    const customerForm = this.getSubform('customer');
    if (customerForm.getDataOfProperty('isFirstPartyDamage')) {
      customerForm.setProperty('gender', GenderEnum.COMPANY);
      customerForm.setProperty('firstName', currentUser.firstName);
      customerForm.setProperty('lastName', currentUser.lastName);
      customerForm.setProperty('isCustomerDeductingVatEntitled', 'yes');
      if (selectedFirm) {
        customerForm.setProperty('companyName', selectedFirm.name);
        customerForm.setProperty('place', selectedFirm.city);
        customerForm.setProperty('phoneGeneric', selectedFirm.phone);
        customerForm.setProperty('postalCode', selectedFirm.postalCode);
        customerForm.setProperty('street', selectedFirm.street);
        customerForm.setProperty('email', selectedFirm.email);
      }
    }
    else {
      this.resetFirstPartyDamageCustomerFields();
    }
  }

  private resetMinimalRecordCreationIfNotAllowed(): void {
    if (!this.isMinimalRecordCreationAllowed()) {
      this.setProperty('minimalRecordCreation', false);
    }
  }

  /**
   * @return true if customer data have been pre-filled, false if not
   */
  private setFirstPartyDamageIfDeliveryClaim(
    currentUser: Record<string, unknown>,
    selectedFirm: Record<string, unknown>
  ): boolean {
    if (this.isDeliveryClaimTypeSelected()) {
      this.getSubform('customer').setProperty('isFirstPartyDamage', true);
      this.updateFirstPartyDamage(currentUser, selectedFirm);
      return true;
    }

    return false;
  }
}
